import {useState} from 'react';
import {getStore} from 'src/v2/state/store';
import {useWalletConnectionClient} from 'src/v2/state/walletConnection';
import {WalletCard} from 'src/v2/components/wallet/WalletCard';
import {NetworkSwitchSelector} from 'src/v2/components/networkSwitch/NetworkSwitchSelector';
import {BrandedButton, Button, Banner} from 'src/v2/components/common';
import {
  getChainIdFromNetwork,
  getNetworkName,
} from 'src/components/Wallet/constants';
import {useUpdateSessionOnWalletConnect} from 'src/v2/components/walletConnect/useUpdateSessionOnWalletConnect';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {WalletRegistry} from 'src/v2/util/WalletRegistry';
import type {SupportedNetwork} from '@stripe-internal/wallet-connect-wrapper';
import {EVM_NETWORKS} from 'src/types';
import {sortNetworksTempoFirst} from 'src/v2/util/sortNetworksTempoFirst';
import {getDisabledNetworksForProvider} from 'src/v2/util/isWalletProviderDisabled';
import {ErrorBanner} from 'src/v2/components/error/ErrorBanner';
import {
  handleError,
  clearErrorBanner,
} from 'src/v2/components/error/handleError';
import {ScreenContentLayout} from 'src/v2/components/screen/ScreenContentLayout';

import {message, useIntl} from '@sail/intl';

const messages = {
  updateNetwork: message({
    id: 'crypto.payins.v2.network_switch.update_network',
    description: 'Button text to switch the blockchain network',
    defaultMessage: 'Update network',
  }),

  unsupportedNetwork: message<{
    supportedNetworks: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.network_switch.unsupported_network',

    description:
      'Error message when the active wallet network is not supported',

    defaultMessage:
      'We only accept {supportedNetworks} for this purchase. Update your network to continue.',
  }),

  processingText: message({
    id: 'crypto.payins.v2.network_switch.processing_text',
    description: 'Button text to switch the blockchain network',
    defaultMessage: 'Processing...',
  }),

  cancelText: message({
    id: 'crypto.payins.v2.network_switch.cancel_text',
    description: 'Button text to cancel the network switch',
    defaultMessage: 'Cancel',
  }),

  changeWallet: message({
    id: 'crypto.payins.v2.network_switch.change_wallet',

    description:
      "Button text to change the wallet when the active wallet's network is not supported",

    defaultMessage: 'Use another wallet',
  }),
};

export const NetworkSwitchScreen = () => {
  const store = getStore();
  const walletClient = useWalletConnectionClient();
  const [updateSessionOnWalletConnect] = useUpdateSessionOnWalletConnect();
  const {mode} = useSessionContext();
  const intl = useIntl();

  const livemode = store.useSelector((state) => state.livemode);
  const activeWallet = store.useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const supportedCurrencies = store.useSelector(
    (state) => state.walletConnectSupportedCurrencies,
  );
  const selectedToken = store.useSelector(
    (state) => state.anyCryptoDeposit.selectedToken,
  );
  const isAnyCryptoWalletConnect = store.useSelector(
    (state) => state.featureFlags.enable_pay_with_any_crypto_wallet_connect,
  );
  const disabledWalletProviders = store.useSelector(
    (state) => state.disabledWalletProviders,
  );

  const disabledNetworksForWallet = activeWallet
    ? getDisabledNetworksForProvider(activeWallet.type, disabledWalletProviders)
    : [];

  const supportedNetworks: SupportedNetwork[] = sortNetworksTempoFirst([
    ...new Set(
      supportedCurrencies
        .filter(
          (currency) =>
            !isAnyCryptoWalletConnect || currency.assetCode === selectedToken,
        )
        .map((currency) => currency.currencyNetwork)
        .filter((network): network is SupportedNetwork =>
          EVM_NETWORKS.includes(network),
        )
        .filter((network) => !disabledNetworksForWallet.includes(network)),
    ),
  ] as SupportedNetwork[]);

  const activeWalletNetwork = activeWallet?.network;

  const isActiveWalletNetworkSupported = activeWalletNetwork
    ? supportedNetworks.includes(activeWalletNetwork)
    : false;

  const defaultNetworkId = activeWalletNetwork || supportedNetworks[0];

  const [selectedNetworkId, setSelectedNetworkId] = useState(defaultNetworkId);
  const [pending, setPending] = useState(false);

  const isSelectedNetworkSupported =
    supportedNetworks.includes(selectedNetworkId);
  const isUpdateButtonDisabled = pending || !isSelectedNetworkSupported;

  const handleNetworkSwitch = async () => {
    if (!activeWallet) {
      return;
    }

    clearErrorBanner();

    setPending(true);
    store.setValue((state) => {
      state.overlay = 'NETWORK_SWITCH';
    });
    try {
      const chainId = getChainIdFromNetwork(selectedNetworkId, livemode);
      await walletClient.switchNetwork(chainId);

      // Update session on wallet connect after successful network switch
      await updateSessionOnWalletConnect({
        walletAddress: activeWallet.address,
        network: selectedNetworkId,
        type: activeWallet.type,
        walletKey: activeWallet.key,
      });

      store.setValue((state) => {
        state.screen = mode === 'setup' ? 'SETUP' : 'PAY';
      });
    } catch (error) {
      handleError({error, intl, context: 'network_switch'});
    } finally {
      setPending(false);
      store.setValue((state) => {
        state.overlay = null;
      });
    }
  };

  const handleCancel = () => {
    store.setValue((state) => {
      state.screen = mode === 'setup' ? 'SETUP' : 'PAY';
    });
  };

  const handleChangeWallet = () => {
    store.setValue((state) => {
      state.screen = 'WALLET_SWITCH';
    });
  };

  // Pre-condition for rendering the screen
  if (!activeWallet) {
    throw new Error('Active wallet not found in NetworkSwitchScreen');
  }

  const walletOption = WalletRegistry.getWalletOption({
    provider: activeWallet.type,
    key: activeWallet.key,
  });

  if (!walletOption) {
    throw new Error('Wallet option not found in NetworkSwitchScreen');
  }

  return (
    <ScreenContentLayout>
      <WalletCard
        wallet={{
          ...activeWallet,
          option: walletOption,
        }}
        hideNetworkBadge
        bottomContent={
          <>
            <NetworkSwitchSelector
              supportedNetworks={supportedNetworks}
              selectedNetworkId={selectedNetworkId}
              onNetworkSelect={setSelectedNetworkId}
            />
            {isActiveWalletNetworkSupported ? null : (
              <Banner
                message={intl.formatMessage(messages.unsupportedNetwork, {
                  supportedNetworks: intl.formatList(
                    supportedNetworks.map((network) => getNetworkName(network)),
                    {type: 'disjunction'},
                  ),
                })}
                variant="error"
              />
            )}
            <ErrorBanner />
            <BrandedButton
              onClick={handleNetworkSwitch}
              loading={pending}
              disabled={isUpdateButtonDisabled}
            >
              {intl.formatMessage(
                messages[pending ? 'processingText' : 'updateNetwork'],
              )}
            </BrandedButton>
            {isActiveWalletNetworkSupported ? (
              <Button
                onClick={handleCancel}
                disabled={pending}
                type="secondary"
              >
                {intl.formatMessage(messages.cancelText)}
              </Button>
            ) : (
              <Button
                onClick={handleChangeWallet}
                disabled={pending}
                type="secondary"
              >
                {intl.formatMessage(messages.changeWallet)}
              </Button>
            )}
          </>
        }
      />
    </ScreenContentLayout>
  );
};
